import React, { useEffect, useState } from 'react';
import { Drawer, Button, message } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import * as actions from "store/actions/AuthActions";
import useIsMobile from 'hooks/useIsMobile';
import dayjs from 'dayjs';

const AddDealsDrawer = ({ visible, onClose, campaignId, onSuccess, existingDealIds = [], parentCampaignDealIds = [] }) => {
    const isMobile = useIsMobile();
    const [deals, setDeals] = useState([]);
    const [selectedDeals, setSelectedDeals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (visible) {
            fetchDeals();
        }
    }, [visible]);

    const fetchDeals = async () => {
        setLoading(true);
        try {
            const response = await actions.loadDeals(1, '', undefined, undefined, MAX_PAGE_SIZE);
            if (response.status === 200) {
                // Filter out deals that are:
                // 1. Already assigned to ANY campaign
                // 2. Assigned to parent campaign (for child campaigns)
                const allDeals = response.data.results || [];
                const filteredDeals = allDeals.filter(deal =>
                    !deal.is_assigned_to_campaign &&
                    !parentCampaignDealIds.includes(deal.id)
                );
                setDeals(filteredDeals);
            }
        } catch (error) {
            message.error('Failed to load deals');
        }
        setLoading(false);
    };

    const handleAddDeals = async () => {
        if (selectedDeals.length === 0) {
            message.warning('Please select at least one deal');
            return;
        }

        setSubmitting(true);
        try {
            for (let dealId of selectedDeals) {
                const data = {
                    campaign: campaignId,
                    deal: dealId,
                };
                await actions.addCampaignDeal(data);
            }
            message.success(`${selectedDeals.length} deal(s) added successfully`);
            setSelectedDeals([]);
            onSuccess();
            onClose();
        } catch (error) {
            message.error('Failed to add deals');
        }
        setSubmitting(false);
    };

    const columns = [
        {
            title: 'Deal Name',
            dataIndex: 'deal_name',
            key: 'deal_name',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (text) => text ? `Rs. ${parseFloat(text).toLocaleString('en-IN')}` : 'Rs. 0',
        },
        {
            title: 'Stage',
            dataIndex: 'stage_display',
            key: 'stage_display',
        },
        {
            title: 'Closing Date',
            dataIndex: 'closing_date',
            key: 'closing_date',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
        },
    ];

    const rowSelection = {
        selectedRowKeys: selectedDeals,
        onChange: (selectedRowKeys) => {
            setSelectedDeals(selectedRowKeys);
        },
    };

    return (
        <Drawer
            title="Add Deals to Campaign"
            placement={isMobile ? "bottom" : "right"}
            width={isMobile ? "100%" : 800}
            height={isMobile ? "90vh" : undefined}
            onClose={onClose}
            open={visible}
            styles={{
                body: { paddingBottom: isMobile ? 80 : 24 }
            }}
            footer={
                <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: '8px' }}>
                    <Button onClick={onClose} style={isMobile ? { flex: 1 } : undefined}>Cancel</Button>
                    <Button
                        type="primary"
                        onClick={handleAddDeals}
                        loading={submitting}
                        style={isMobile ? { flex: 1 } : undefined}
                    >
                        {isMobile ? "Add Deals" : "Add Selected Deals"}
                    </Button>
                </div>
            }
        >
            <Table
                rowSelection={{
                    type: 'checkbox',
                    ...rowSelection,
                }}
                columns={columns}
                dataSource={deals}
                loading={loading}
                rowKey="id"
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                size="small"
            />
        </Drawer>
    );
};

export default AddDealsDrawer;
